import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Images } from '../../utils/imageConstants'
import { Api } from '../../services/interceptor'
import { QUERY_KEY } from '../../services/queryEndPoint'
import toast from 'react-hot-toast'
import type {
  Message,
  FileWithKey,
  PresignedUrlResponse,
  ChatSession,
  ChatHistoryResponse,
  ChatMessagesResponse
} from './types'
import {
  generateSessionId,
  getContentType,
  getFileType,
  getFileIcon,
  getFileTypeLabel
} from './utils'
import ChatSidebar from './components/ChatSidebar'
import SidebarArrow from './components/SidebarArrow'
import BackgroundBatman from './components/BackgroundBatman'
import WelcomeSection from './components/WelcomeSection'
import MessageList from './components/MessageList'
import SearchBar from './components/SearchBar'

const StatGpt = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [messages, setMessages] = useState<Message[]>([])
  const [sessionId, setSessionId] = useState<string>('')
  const [selectedFiles, setSelectedFiles] = useState<FileWithKey[]>([])
  const [isSending, setIsSending] = useState(false)
  const [isUploadingFiles, setIsUploadingFiles] = useState(false)
  
  // Chat history states
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([])
  const [selectedChatId, setSelectedChatId] = useState<string>('')
  const [chatHistoryPage, setChatHistoryPage] = useState(0)
  const [chatHistoryHasMore, setChatHistoryHasMore] = useState(true)
  const [isLoadingChatHistory, setIsLoadingChatHistory] = useState(false)
  
  // Messages pagination states
  const [messagesPage, setMessagesPage] = useState(0)
  const [messagesHasMore, setMessagesHasMore] = useState(true)
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [isNewChatMode, setIsNewChatMode] = useState(false)
  const [isSidebarOpen, setIsSidebarOpen] = useState(true) // Sidebar open/closed state
  
  const fileInputRef = useRef<HTMLInputElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollingRetryCountRef = useRef<number>(0)
  const sidebarScrollRef = useRef<HTMLDivElement>(null)
  const messagesScrollRef = useRef<HTMLDivElement>(null)

  const suggestedQuestions = [
    {
      icon: Images.highest_score_icon,
      text: "Highest runs in the tournament"
    },
    {
      icon: Images.most_wickets_icon,
      text: "Who has taken the most wickets in this tournament?"
    },
    {
      icon: Images.most_wickets_icon,
      text: "Which team scored the most sixes in the league?"
    },
    {
      icon: Images.top_all_rounder_icon,
      text: "Who is the top all-rounder of the season A?"
    }
  ]

  // Fetch chat history
  const fetchChatHistory = useCallback(async (page: number, size: number = 20) => {
    if (isLoadingChatHistory || (!chatHistoryHasMore && page > 0)) return

    setIsLoadingChatHistory(true)
    try {
      const response = await Api.get(QUERY_KEY.statGptGetUserMessageHistory, {
        params: {
          page,
          size
        }
      })
      const data: ChatHistoryResponse = response?.data
      if (data?.content) {
        if (page === 0) {
          setChatSessions(data.content)
        } else {
          setChatSessions(prev => [...prev, ...data.content])
        }
        setChatHistoryHasMore(!data.last)
      }
    } catch (error) {
      console.error('Error fetching chat history:', error)
    } finally {
      setIsLoadingChatHistory(false)
    }
  }, [isLoadingChatHistory, chatHistoryHasMore])

  // Fetch messages for a session
  const fetchMessages = useCallback(async (sessionId: string, page: number, size: number = 20, append: boolean = false) => {
    if (!sessionId || isLoadingMessages || (!messagesHasMore && page > 0)) return

    setIsLoadingMessages(true)
    try {
      const response = await Api.get(`${QUERY_KEY.statGptGetChatMessages}/${sessionId}/messages`, {
        params: {
          page,
          size
        }
      })
      const data: ChatMessagesResponse = response?.data

      if (data?.content) {
        // Reverse the array so oldest messages come first (first object in API is most recent)
        const reversedContent = [...data.content].reverse()
        const formattedMessages: Message[] = reversedContent.flatMap(msg => {
          const messages: Message[] = []
          if (msg.questionText) {
            messages.push({
              id: `user-${msg.messageId || Date.now()}`,
              text: msg.questionText,
              timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
              type: 'user'
            })
          }
          
          // Handle AI response based on status
          const status = msg.status
          if (status === 'completed' && msg.responseText) {
            // Show responseText when status is completed
            messages.push({
              id: `ai-${msg.messageId || Date.now()}`,
              text: msg.responseText,
              timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
              type: 'ai',
              isTyping: false,
              status: 'completed'
            })
          } else if (status === 'error') {
            // Show error message when status is error
            messages.push({
              id: `ai-${msg.messageId || Date.now()}`,
              text: 'Please Try Again!',
              timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
              type: 'ai',
              isTyping: false,
              status: 'error'
            })
          } else if (status === 'pending' || status === 'processing') {
            // Show typing indicator for pending/processing status
            messages.push({
              id: `ai-${msg.messageId || Date.now()}`,
              text: status === 'pending' ? 'StatGPT is thinking...' : 'StatGPT is typing...',
              timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
              type: 'ai',
              isTyping: true,
              status: status
            })
          } else if (msg.responseText) {
            // Fallback: if responseText exists but no status, show it (backward compatibility)
            messages.push({
              id: `ai-${msg.messageId || Date.now()}`,
              text: msg.responseText,
              timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
              type: 'ai',
              isTyping: false,
              status: 'completed' // Assume completed if responseText exists
            })
          }
          
          return messages
        })

        if (append) {
          // When appending (loading next page), append to the end
          setMessages(prev => [...prev, ...formattedMessages])
        } else {
          // When loading fresh, set messages normally
          setMessages(formattedMessages)
        }
        setMessagesHasMore(!data.last)
      }
    } catch (error) {
      console.error('Error fetching messages:', error)
    } finally {
      setIsLoadingMessages(false)
      setIsInitialLoad(false)
    }
  }, [isLoadingMessages, messagesHasMore])

  // Initialize: Load chat history and select first chat
  useEffect(() => {
    fetchChatHistory(0)
  }, [])

  // Select first chat by default when chat history loads (wait for loading to complete)
  // Only auto-select if not in new chat mode
  useEffect(() => {
    if (chatSessions.length > 0 && !selectedChatId && isInitialLoad && !isLoadingChatHistory && !isNewChatMode) {
      const firstChatId = chatSessions[0].sessionId
      if (firstChatId) {
        setSelectedChatId(firstChatId)
        setSessionId(firstChatId)
        fetchMessages(firstChatId, 0)
      }
    }
  }, [chatSessions, selectedChatId, isInitialLoad, isLoadingChatHistory, isNewChatMode, fetchMessages])

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      clearPolling()
    }
  }, [])

  // Scroll to bottom when messages change (only for new messages, not when loading history)
  useEffect(() => {
    if (messagesScrollRef.current && (!isLoadingMessages || messagesPage === 0)) {
      // Scroll the container itself, not the entire page
      const container = messagesScrollRef.current
      container.scrollTop = container.scrollHeight
    }
  }, [messages, isLoadingMessages, messagesPage])

  // Handle chat selection
  const handleChatSelect = (chatId: string) => {
    if (!chatId || chatId === selectedChatId) return

    setSelectedChatId(chatId)
    setSessionId(chatId)
    setMessages([])
    setMessagesPage(0)
    setMessagesHasMore(true)
    setIsInitialLoad(true)
    setIsNewChatMode(false) // Exit new chat mode when selecting a chat
    fetchMessages(chatId, 0)
    
    // Clear any ongoing polling
    clearPolling()
  }

  // Handle new chat - create new session
  const handleNewChat = () => {
    // Clear messages
    setMessages([])
    // Generate new session ID
    const newSessionId = generateSessionId()
    setSessionId(newSessionId)
    setSelectedChatId('')
    setMessagesPage(0)
    setMessagesHasMore(true)
    setIsInitialLoad(false) // Set to false to show welcome screen immediately
    setIsNewChatMode(true) // Enable new chat mode to show welcome screen
    sessionStorage.setItem('statGptSessionId', newSessionId)
    // Clear selected files
    setSelectedFiles([])
    // Clear any ongoing polling
    clearPolling()
    // Refresh chat history to include new chat
    fetchChatHistory(0)
  }

  // Infinite scroll for sidebar
  const handleSidebarScroll = useCallback(() => {
    if (!sidebarScrollRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = sidebarScrollRef.current
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50

    if (isNearBottom && chatHistoryHasMore && !isLoadingChatHistory) {
      const nextPage = chatHistoryPage + 1
      setChatHistoryPage(nextPage)
      fetchChatHistory(nextPage)
    }
  }, [chatHistoryHasMore, isLoadingChatHistory, chatHistoryPage, fetchChatHistory])

  // Helper function to clear polling
  const clearPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
    pollingRetryCountRef.current = 0
  }

  // Infinite scroll for messages
  const handleMessagesScroll = useCallback(() => {
    if (!messagesScrollRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = messagesScrollRef.current
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100

    // Load more when scrolling near bottom (for next page of messages)
    if (isNearBottom && messagesHasMore && !isLoadingMessages) {
      const nextPage = messagesPage + 1
      setMessagesPage(nextPage)
      fetchMessages(sessionId, nextPage, 20, true)
    }
  }, [messagesHasMore, isLoadingMessages, messagesPage, sessionId, fetchMessages])

  // Short polling function to get message response
  const pollForResponse = async (messageId: string, typingMessageId: string) => {
    try {
      // Increment retry count
      pollingRetryCountRef.current += 1

      // Check if we've exceeded max retries (10 attempts)
      if (pollingRetryCountRef.current > 10) {
        clearPolling()
        setMessages(prev => prev.map(msg => 
          msg.id === typingMessageId 
            ? { ...msg, text: 'Please Try Again!', isTyping: false, status: 'error' }
            : msg
        ))
        setIsSending(false)
        return
      }

      const response = await Api.get(`${QUERY_KEY.statGptGetMessage}/${messageId}`)
      const responseData = response?.data
      const status = responseData?.status

      // Handle different statuses
      if (status === 'completed') {
        // Clear polling interval
        clearPolling()

        // Update the typing message with actual response
        setMessages(prev => prev.map(msg => 
          msg.id === typingMessageId 
            ? { ...msg, text: responseData.responseText || 'No response received.', isTyping: false, status: 'completed' }
            : msg
        ))
        setIsSending(false)
        
        // Refresh chat history to update thumbnail
        fetchChatHistory(0)
      } else if (status === 'error') {
        // Stop polling on error
        clearPolling()
        setMessages(prev => prev.map(msg => 
          msg.id === typingMessageId 
            ? { ...msg, text: 'Please Try Again!', isTyping: false, status: 'error' }
            : msg
        ))
        setIsSending(false)
      } else if (status === 'pending') {
        // Update message to show "thinking"
        setMessages(prev => prev.map(msg => 
          msg.id === typingMessageId 
            ? { ...msg, text: 'StatGPT is thinking...', isTyping: true, status: 'pending' }
            : msg
        ))
      } else if (status === 'processing') {
        // Update message to show "typing"
        setMessages(prev => prev.map(msg => 
          msg.id === typingMessageId 
            ? { ...msg, text: 'StatGPT is typing...', isTyping: true, status: 'processing' }
            : msg
        ))
      }
      // If status is not one of the above, continue polling (for backward compatibility)
    } catch (error) {
      console.error('Error polling for response:', error)
      // Check retry count on error too
      if (pollingRetryCountRef.current >= 10) {
        clearPolling()
        setMessages(prev => prev.map(msg => 
          msg.id === typingMessageId 
            ? { ...msg, text: 'Please Try Again!', isTyping: false, status: 'error' }
            : msg
        ))
        setIsSending(false)
      }
      // Otherwise keep polling (will retry in next interval)
    }
  }

  const handleSendMessage = async () => {
    // Allow sending if there's text OR files (like ChatGPT)
    if ((searchQuery.trim() || selectedFiles.length > 0) && !isSending) {
      const question = searchQuery.trim() || ''
      
      if (!sessionId) {
        alert('Session ID is missing. Please wait a moment and try again.')
        return
      }

      // Exit new chat mode when sending first message
      if (isNewChatMode) {
        setIsNewChatMode(false)
      }

      setIsSending(true)

      try {
        // Step 1: Upload all files first (if any)
        let uploadedFiles: FileWithKey[] = []
        
        if (selectedFiles.length > 0) {
          // Mark all files as uploading
          setSelectedFiles(prev => prev.map(f => ({ ...f, isUploading: true })))

          // Prepare files payload for presigned URL API
          const filesPayload = selectedFiles.map(fileWithKey => ({
            fileName: fileWithKey.file.name,
            contentType: getContentType(fileWithKey.file)
          }))

          // Get presigned URLs
          const presignedResponse = await Api.post(QUERY_KEY.statGptGeneratePresignedUrl, {
            sessionId: sessionId,
            files: filesPayload
          })

          const presignedData: PresignedUrlResponse[] = presignedResponse?.data || []
          
          if (presignedData.length !== selectedFiles.length) {
            throw new Error(`Expected ${selectedFiles.length} presigned URLs but got ${presignedData.length}`)
          }

          // Upload files to presigned URLs sequentially
          uploadedFiles = await Promise.all(
            selectedFiles.map(async (fileWithKey, index) => {
              const presigned = presignedData[index]
              if (!presigned?.url || !presigned?.key) {
                throw new Error(`No presigned URL/key for file ${fileWithKey.file.name}`)
              }

              const contentType = getContentType(fileWithKey.file)
              
              // Upload file to presigned URL using PUT
              const uploadResponse = await fetch(presigned.url, {
                method: 'PUT',
                body: fileWithKey.file,
                headers: {
                  'Content-Type': contentType
                }
              })

              if (!uploadResponse.ok) {
                let errorText = ''
                try {
                  errorText = await uploadResponse.text()
                } catch (e) {
                  errorText = uploadResponse.statusText
                }
                throw new Error(`Failed to upload ${fileWithKey.file.name}: ${uploadResponse.status} ${uploadResponse.statusText}`)
              }

              return {
                ...fileWithKey,
                key: presigned.key,
                isUploading: false
              }
            })
          )
        }

        // Step 2: Create user message with uploaded files
        const userMessage: Message = {
          id: Date.now().toString(),
          text: question,
          timestamp: new Date(),
          type: 'user',
          files: uploadedFiles.length > 0 ? uploadedFiles : undefined
        }
        
        // Add user message to chat
        setMessages(prev => [...prev, userMessage])
        setSearchQuery('')
        setSelectedFiles([]) // Clear selected files after sending

        // Step 3: Add AI typing indicator
        const typingMessageId = `typing-${Date.now()}`
        const typingMessage: Message = {
          id: typingMessageId,
          text: 'StatGPT is typing...',
          timestamp: new Date(),
          type: 'ai',
          isTyping: true,
          status: 'processing'
        }
        setMessages(prev => [...prev, typingMessage])

        // Step 4: Send message API call
        const fileKeys = uploadedFiles.map(fileWithKey => fileWithKey.key!)
        const requestBody = {
          question: question,
          sessionId: sessionId,
          files: fileKeys.length > 0 ? fileKeys : null,
          createdAt: new Date().toISOString()
        }

        const sendResponse = await Api.post(QUERY_KEY.statGptSendMessage, requestBody)
        const messageId = sendResponse?.data?.messageId || sendResponse?.data?.id

        if (messageId) {
          // Reset retry count for new polling
          pollingRetryCountRef.current = 0
          
          // Start short polling (5 second intervals)
          pollingIntervalRef.current = setInterval(() => {
            pollForResponse(messageId, typingMessageId)
          }, 5000)

          // Also poll immediately (don't wait 5 seconds for first poll)
          pollForResponse(messageId, typingMessageId)
        } else {
          // If no messageId, show error
        setMessages(prev => prev.map(msg => 
          msg.id === typingMessageId 
            ? { ...msg, text: 'Sorry, something went wrong. Please try again.', isTyping: false, status: 'error' }
            : msg
        ))
          setIsSending(false)
        }
      } catch (error) {
        console.error('Error sending message:', error)
        // Reset uploading state
        setSelectedFiles(prev => prev.map(f => ({ ...f, isUploading: false, uploadError: error instanceof Error ? error.message : 'Upload failed' })))
        setIsSending(false)
        
        // Clear polling if it was started
        clearPolling()
        
        alert(error instanceof Error ? error.message : 'Failed to send message. Please try again.')
      }
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && (searchQuery.trim() || selectedFiles.length > 0)) {
      e.preventDefault() // Prevent form submission or page refresh
      handleSendMessage()
    }
  }

  const handleSuggestedQuestionClick = (questionText: string) => {
    setSearchQuery(questionText)
    // Focus the input field after setting the text
    setTimeout(() => {
      const inputElement = document.querySelector('.statgpt-search-input') as HTMLInputElement
      if (inputElement) {
        inputElement.focus()
      }
    }, 0)
  }

  const handleRemoveFile = (indexToRemove: number) => {
    setSelectedFiles(prev => {
      const newFiles = prev.filter((_, index) => index !== indexToRemove)
      // Clean up preview URLs for removed images
      const removedFile = prev[indexToRemove]
      if (removedFile?.preview) {
        URL.revokeObjectURL(removedFile.preview)
      }
      return newFiles
    })
  }


  const handleMediaClick = () => {
    // Open file picker directly
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) {
      return
    }

    const fileArray = Array.from(files)

    try {
      // Store files locally first (no upload yet) - upload when user sends message
      const filesWithPreview: FileWithKey[] = fileArray.map(file => {
        const fileType = getFileType(file)
        let preview: string | undefined
        
        // Create preview URL for images
        if (fileType === 'image') {
          preview = URL.createObjectURL(file)
        }

        return {
          file: file,
          fileType: fileType,
          preview: preview,
          isUploading: false
        }
      })

      // Store files locally (don't upload yet)
      setSelectedFiles(prev => [...prev, ...filesWithPreview])

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (error: any) {
      console.error('Error selecting files:', error)
      alert('Failed to select files. Please try again.')
    }
  }

  const handleCopyAnswer = async (text: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.focus()
        textarea.select()
        try {
          document.execCommand('copy')
          toast.success('Answer copied to clipboard')
        } finally {
          document.body.removeChild(textarea)
        }
      }
    } catch (error) {
      console.error('Error copying text:', error)
      toast.error('Failed to copy answer')
    }
  }

  return (
    <div className="statgpt-wrapper">
      <div className="statgpt-layout">
        <ChatSidebar
          isOpen={isSidebarOpen}
          chatSessions={chatSessions}
          selectedChatId={selectedChatId}
          isLoading={isLoadingChatHistory}
          onNewChat={handleNewChat}
          onChatSelect={handleChatSelect}
          onScroll={handleSidebarScroll}
          sidebarScrollRef={sidebarScrollRef}
        />
        
        <SidebarArrow
          isOpen={isSidebarOpen}
          onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        />

        <div className={`statgpt-content-section ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
          <BackgroundBatman />
          
          {messages.length === 0 && (isNewChatMode || (!selectedChatId && !isInitialLoad)) && !isLoadingMessages && (
            <WelcomeSection
              suggestedQuestions={suggestedQuestions}
              onQuestionClick={handleSuggestedQuestionClick}
            />
          )}

          {messages.length > 0 && (
            <MessageList
              messages={messages}
              isLoading={isLoadingMessages}
              messagesPage={messagesPage}
              onScroll={handleMessagesScroll}
              messagesScrollRef={messagesScrollRef}
              messagesEndRef={messagesEndRef}
              getFileIcon={getFileIcon}
              getFileTypeLabel={getFileTypeLabel}
              onCopyAnswer={handleCopyAnswer}
            />
          )}

          {isInitialLoad && messages.length === 0 && (
            <div className="statgpt-loading-container">
              <div className="statgpt-loading-text">Loading messages...</div>
            </div>
          )}

          <SearchBar
            searchQuery={searchQuery}
            selectedFiles={selectedFiles}
            onSearchChange={setSearchQuery}
            onKeyPress={handleKeyPress}
            onMediaClick={handleMediaClick}
            onSendMessage={handleSendMessage}
            onRemoveFile={handleRemoveFile}
            getFileIcon={getFileIcon}
          />

          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            accept=".csv,.xlsx,.xls,.mp4"
          />
        </div>
      </div>
    </div>
  )
}

export default StatGpt
